import { createSlice } from '@reduxjs/toolkit';
import { COPY_INFO } from '../../constants/projectConstants';

// Copied edits auto-expire after this long. The timer is refreshed on every
// new copy (setCopyInfo stamps a fresh expiresAt).
export const COPY_INFO_TTL_MS = 60 * 60 * 1000; // 1 hour

const DEFAULT_COPY_INFO = {
  hasCopiedEdits: false,
  id: null,
  expiresAt: null,
};

// Read copyInfo from localStorage so that edits copied in one tab are
// recognised in other tabs of the same browser (the edit data itself
// already lives on the shared OS clipboard). Expired entries are dropped.
export const readCopyInfoFromStorage = () => {
  try {
    const stored = localStorage.getItem(COPY_INFO);
    if (!stored) return { ...DEFAULT_COPY_INFO };
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === 'object') {
      // Drop expired copies so a stale "Paste & Apply" button never shows.
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(COPY_INFO);
        return { ...DEFAULT_COPY_INFO };
      }
      return { ...DEFAULT_COPY_INFO, ...parsed };
    }
  } catch (error) {
    console.warn('Failed to read copyInfo from storage:', error);
  }
  return { ...DEFAULT_COPY_INFO };
};

const app = createSlice({
  name: 'app',
  initialState: {
    bookADemoFormOpen: false,
    feedbackDialogOpen: false,
    contactSupportFormOpen: false,
    configuratorRequestFormOpen: false,
    uploadedFiles: {},
    completedFiles: [],
    showPopupBlockModal: false,
    useCacheInAPI: localStorage.getItem("CACHE_TIMESTAMP") ? JSON.parse(localStorage.getItem("CACHE_TIMESTAMP")) : Date.now(),
    copyInfo: readCopyInfoFromStorage(),
  },
  reducers: {
    setBookADemoFormOpen: (state, { payload }) => {
      state.bookADemoFormOpen = payload;
    },
    setFeedbackDialogOpen: (state, { payload }) => {
      state.feedbackDialogOpen = payload;
    },
    setContactSupportFormOpen: (state, { payload }) => {
      state.contactSupportFormOpen = payload;
    },
    setConfiguratorRequestFormOpen: (state, { payload }) => {
      state.configuratorRequestFormOpen = payload;
    },
    setUploadedFiles: (state, { payload }) => {
      state.uploadedFiles.files = payload;
    },
    addFiles: (state, { payload }) => {
      state.uploadedFiles = { ...state.uploadedFiles, ...payload };
    },
    updateFile: (state, { payload }) => {
      if (state.uploadedFiles?.files?.[payload?.id]) {
        state.uploadedFiles.files[payload?.id] = payload;
      }
    },
    setCompletedFiles: (state, { payload }) => {
      state.completedFiles = [...state.completedFiles, payload];
    },
    // Patch specific upload rows by their client id: `[{ id, ...fields }]`. Used by the background
    // resume to report an outcome back to the progress card, which otherwise keeps showing whatever
    // the original (failed) upload attempt left behind. Rows the card no longer holds are skipped.
    patchUploadedFiles: (state, { payload }) => {
      const files = state.uploadedFiles?.files;
      if (!files) return;
      (payload || []).forEach(({ id, ...fields }) => {
        if (id != null && files[id]) Object.assign(files[id], fields);
      });
    },
    setResetFiles: (state) => {
      state.uploadedFiles = {};
      state.completedFiles = [];
    },
    setShowPopupBlockModal: (state, { payload }) => {
      state.showPopupBlockModal = payload;
    },
    setUseCacheInAPI: (state, { payload }) => {
      state.useCacheInAPI = payload;
      localStorage.setItem("CACHE_TIMESTAMP", payload);
    },
    setCopyInfo: (state, { payload }) => {
      // Stamp/refresh the expiry on every copy so the 1-hour window restarts.
      const next = payload?.hasCopiedEdits
        ? { ...payload, expiresAt: Date.now() + COPY_INFO_TTL_MS }
        : { ...payload };
      state.copyInfo = next;
      try {
        localStorage.setItem(COPY_INFO, JSON.stringify(next));
      } catch (error) {
        console.warn('Failed to persist copyInfo to storage:', error);
      }
    },
    // Sync copyInfo from another tab's localStorage change without
    // re-writing storage (avoids a write/event loop across tabs).
    syncCopyInfoFromStorage: (state, { payload }) => {
      state.copyInfo = payload;
    },
    // Clear copied edits (used on expiry); also removes the persisted copy.
    clearCopyInfo: (state) => {
      state.copyInfo = { ...DEFAULT_COPY_INFO };
      try {
        localStorage.removeItem(COPY_INFO);
      } catch (error) {
        console.warn('Failed to clear copyInfo from storage:', error);
      }
    },
  },
});

export const {
  setBookADemoFormOpen,
  setFeedbackDialogOpen,
  setContactSupportFormOpen,
  setConfiguratorRequestFormOpen,
  setUploadedFiles,
  addFiles,
  updateFile,
  setResetFiles,
  setCompletedFiles,
  patchUploadedFiles,
  setShowPopupBlockModal,
  setUseCacheInAPI,
  setCopyInfo,
  syncCopyInfoFromStorage,
  clearCopyInfo,
} = app.actions;
export const appSelector = (state) => state.app;
export const appReducer = app.reducer;
export default appReducer;

export const selectBookADemoFormOpen = (state) => state.app.bookADemoFormOpen;
export const selectFeedbackDialogOpen = (state) => state.app.feedbackDialogOpen;
export const selectContactSupportFormOpen = (state) => state.app.contactSupportFormOpen;
export const selectConfiguratorRequestFormOpen = (state) => state.app.configuratorRequestFormOpen;
export const selectCompletedFiles = (state) => state.app.completedFiles;
export const selectUploadCompleted = (state) => {
  if (
    Object.keys(state.app.uploadedFiles?.files || {})?.length === 0 ||
    state.app.completedFiles?.length === 0
  )
    return false;
  return state.app.completedFiles.length === Object.keys(state.app.uploadedFiles?.files)?.length;
};

export const selectUploadedFiles = (state) => {
  return Object.fromEntries(
    Object.entries(state.app.uploadedFiles).filter(([key]) => key !== 'undefined')
  );
};
export const selectShowPopupBlockModal = (state) => state.app.showPopupBlockModal;
export const selectIsCacheUsedInAPI = (state) => state.app.useCacheInAPI;
export const selectCopyInfo = (state) => state.app.copyInfo;